import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  EmptyState,
  FilterChips,
  StatusBadge,
} from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { useOptionalAuth } from "@/hooks/useAuth";
import { portalAbsorptionApi } from "@/api/portalAbsorption";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { NotificationComposeModal } from "./NotificationComposeModal";

// Portal-absorption §2.5 — "Notifications for this account": every broadcast
// DELIVERED to one portal account, rendered as read-receipt rows (sender,
// channel, read state + date) with an All/Unread filter. Composing a targeted
// notification happens in place via NotificationComposeModal (gated on
// Portal.ManageAccounts — hidden, not disabled, when lacking).

export interface AccountNotificationsPanelProps {
  portalUserId: string;
  /** Hide the compose affordance (e.g. when the mount already offers it). */
  hideComposeLink?: boolean;
}

type ReadFilter = "all" | "unread";

export function AccountNotificationsPanel({
  portalUserId,
  hideComposeLink,
}: AccountNotificationsPanelProps) {
  const { date } = useDateFormatter();
  const [filter, setFilter] = useState<ReadFilter>("all");
  const [composeOpen, setComposeOpen] = useState(false);

  // Self-gated (AdminUndoButton precedent) — the panel is a staff surface,
  // but useOptionalAuth keeps it safe under provider-less test mounts.
  const auth = useOptionalAuth();
  const canCompose = auth?.hasPermission("Portal.ManageAccounts") ?? false;

  const feed = useQuery({
    queryKey: portalAbsorptionKeys.accountNotifications(portalUserId),
    queryFn: () => portalAbsorptionApi.notificationsForAccount(portalUserId),
    enabled: Boolean(portalUserId),
  });

  const rows = feed.data ?? [];
  const unread = useMemo(() => rows.filter((n) => !n.readAt), [rows]);
  const visible = filter === "unread" ? unread : rows;

  return (
    <div className="flex flex-col gap-space-3">
      {!hideComposeLink && canCompose && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setComposeOpen(true)}
          >
            Send a notification
          </Button>
        </div>
      )}
      {feed.isLoading ? (
        <p className="text-caption text-gray-mid">Loading notifications…</p>
      ) : feed.isError ? (
        <p className="text-caption text-gray-mid">Could not load this account's notifications.</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No notifications delivered"
          description="Broadcasts that include this account will appear here with their read state."
        />
      ) : (
        <>
          <FilterChips
            label="Filter notifications"
            chips={[
              { id: "all", label: "All", count: rows.length },
              { id: "unread", label: "Unread", count: unread.length },
            ]}
            activeId={filter}
            onChange={(id) => setFilter(id as ReadFilter)}
          />
          {visible.length === 0 ? (
            <p className="text-caption text-gray-mid">
              Nothing unread — this account has read every notification.
            </p>
          ) : (
            <ul className="flex flex-col gap-space-2">
              {visible.map((n) => (
                <li
                  key={n.id}
                  className="flex flex-col gap-space-1 rounded border border-gray-mid/20 p-space-3"
                >
                  <div className="flex flex-wrap items-center gap-space-2">
                    <span className="font-semibold text-onyx">{n.title}</span>
                    {n.readAt ? (
                      <StatusBadge tone="ok">Read · {date(n.readAt)}</StatusBadge>
                    ) : (
                      <StatusBadge tone="info">Unread</StatusBadge>
                    )}
                  </div>
                  <p className="text-caption text-gray-mid">{n.body}</p>
                  <p className="text-caption text-gray-mid">
                    Sent {date(n.createdAt)} by {n.createdByUserName ?? "—"}
                    {n.sentEmail !== undefined && (
                      <> · {n.sentEmail ? "In-app + email" : "In-app"}</>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {composeOpen && (
        <NotificationComposeModal
          open
          onClose={() => setComposeOpen(false)}
          portalUserId={portalUserId}
        />
      )}
    </div>
  );
}
